import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, AlertTriangle } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { applyAuth } = useAuth();
  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}/info`);
        setInfo(data);
      } catch (e) {
        setLoadErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/invites/${token}/accept`, {
        name: form.name.trim(),
        password: form.password,
      });
      applyAuth(data);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream-50 px-6 py-12">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-10">
          <div className="h-8 w-8 rounded-md bg-forest-700 grid place-items-center">
            <span className="font-heading text-cream-50 text-sm font-semibold">3</span>
          </div>
          <div className="leading-tight">
            <div className="font-heading text-[15px] font-semibold text-forest-800">Sign-Off</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">Charity expense approvals</div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center text-ink-soft"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading invitation…</div>
        ) : loadErr ? (
          <div data-testid="invite-error" className="text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-rose-50 grid place-items-center mb-4">
              <AlertTriangle className="h-5 w-5 text-rose-500" />
            </div>
            <h1 className="font-heading text-2xl text-forest-900 mb-2">Invitation unavailable</h1>
            <p className="text-sm text-ink-soft mb-6">{loadErr}</p>
            <Link to="/login" className="text-forest-700 font-medium hover:underline">Go to sign in</Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-forest-700 mb-3">
              <MailCheck className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wider font-semibold">You're invited</span>
            </div>
            <h1 className="font-heading text-3xl text-forest-900 mb-2 tracking-tight">
              Join {info.organisation_name}
            </h1>
            <p className="text-sm text-ink-soft mb-8">
              Set a password to create your account for <strong>{info.email}</strong>.
            </p>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="accept-invite-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-ink-soft">Email</Label>
                <Input value={info.email} disabled data-testid="invite-email-display" className="bg-cream-100 border-cream-200 text-ink-soft" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-ink-soft">Your name</Label>
                <Input id="name" required value={form.name} onChange={set("name")} data-testid="invite-name-input"
                  className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-ink-soft">Password (min 8 characters)</Label>
                <Input id="password" type="password" required minLength={8} value={form.password} onChange={set("password")} data-testid="invite-password-input"
                  className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700" />
              </div>

              {error && (
                <div data-testid="invite-submit-error" className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <Button type="submit" disabled={busy} data-testid="invite-submit-button"
                className="w-full bg-forest-700 hover:bg-forest-800 text-cream-50 h-11 font-medium">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept & create account"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
